import { Component } from '@theme/component';

class DevCollectionComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this._filterTimeout = null;
    this._isLoading = false;
    this._onPopState = () => {
      this.#fetchAndReplace(new URL(window.location.href));
    };
    window.addEventListener('popstate', this._onPopState);

    // Init price slider positions
    this.#syncAllPriceSliders();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this._onPopState);
  }

  // ── Mobile filter drawer ──

  openDrawer() {
    if (!this.refs.filterSidebar) return;
    this.refs.filterSidebar.dataset.state = 'open';
    document.body.style.overflow = 'hidden';
  }

  closeDrawer() {
    if (!this.refs.filterSidebar) return;
    this.refs.filterSidebar.dataset.state = 'closed';
    document.body.style.overflow = '';
  }

  // ── Sort dropdown ──

  toggleSort() {
    const dropdown = this.refs.sortDropdown;
    const chevron = this.refs.sortChevron;
    if (!dropdown) return;

    const isOpen = !dropdown.classList.contains('hidden');
    if (isOpen) {
      this.#closeSort();
    } else {
      dropdown.classList.remove('hidden');
      dropdown.classList.add('flex', 'flex-col');
      if (chevron) chevron.style.transform = 'rotate(180deg)';
      // Close on outside click
      setTimeout(() => {
        this._closeSortBound = (e) => {
          if (!this.refs.sortWrapper?.contains(e.target)) this.#closeSort();
        };
        document.addEventListener('click', this._closeSortBound);
      }, 0);
    }
  }

  selectSort(event) {
    const btn = event.target.closest('[data-sort-value]');
    if (!btn) return;
    const value = btn.dataset.sortValue;

    // Update label
    if (this.refs.sortLabel) {
      this.refs.sortLabel.textContent = btn.textContent.trim();
    }

    this.#closeSort();

    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', value);
    this.#fetchAndReplace(url);
  }

  #closeSort() {
    const dropdown = this.refs.sortDropdown;
    const chevron = this.refs.sortChevron;
    if (dropdown) {
      dropdown.classList.add('hidden');
      dropdown.classList.remove('flex', 'flex-col');
    }
    if (chevron) chevron.style.transform = '';
    if (this._closeSortBound) {
      document.removeEventListener('click', this._closeSortBound);
      this._closeSortBound = null;
    }
  }

  // ── Filter checkbox change ──

  handleFilterChange() {
    this.#debouncedSubmit();
  }

  // ── Price slider drag ──

  handlePriceSlider(event) {
    const input = event.target;
    const slider = input.closest('.dev-price-slider');
    if (!slider) return;

    const minRange = slider.querySelector('input[data-price-role="min"]');
    const maxRange = slider.querySelector('input[data-price-role="max"]');

    // Prevent min > max
    let minVal = parseInt(minRange.value, 10);
    let maxVal = parseInt(maxRange.value, 10);
    if (minVal > maxVal) {
      if (input.dataset.priceRole === 'min') {
        minRange.value = maxVal;
        minVal = maxVal;
      } else {
        maxRange.value = minVal;
        maxVal = minVal;
      }
    }

    // Sync number inputs
    const form = slider.closest('form');
    if (form) {
      const minInput = form.querySelector('input[type="number"][data-price-role="min"]');
      const maxInput = form.querySelector('input[type="number"][data-price-role="max"]');
      if (minInput) minInput.value = minVal;
      if (maxInput) maxInput.value = maxVal;
    }

    // Update track highlight
    this.#updatePriceTrack(slider, minVal, maxVal);

    // Submit on change (mouseup/touchend), not on every input tick
    if (event.type === 'change') {
      this.#debouncedSubmit();
    }
  }

  // ── Price number input change ──

  handlePriceInput(event) {
    const input = event.target;
    const form = input.closest('form');
    const slider = form?.querySelector('.dev-price-slider');

    if (slider) {
      const minRange = slider.querySelector('[data-price-role="min"]');
      const maxRange = slider.querySelector('[data-price-role="max"]');
      const minInput = form.querySelector('input[type="number"][data-price-role="min"]');
      const maxInput = form.querySelector('input[type="number"][data-price-role="max"]');

      const rangeMax = parseInt(slider.dataset.rangeMax, 10);
      const rangeMin = parseInt(slider.dataset.rangeMin, 10);

      let minVal = parseInt(minInput?.value || rangeMin, 10) || rangeMin;
      let maxVal = parseInt(maxInput?.value || rangeMax, 10) || rangeMax;

      // Clamp
      minVal = Math.max(rangeMin, Math.min(minVal, rangeMax));
      maxVal = Math.max(rangeMin, Math.min(maxVal, rangeMax));
      if (minVal > maxVal) [minVal, maxVal] = [maxVal, minVal];

      if (minRange) minRange.value = minVal;
      if (maxRange) maxRange.value = maxVal;
      this.#updatePriceTrack(slider, minVal, maxVal);
    }

    this.#debouncedSubmit();
  }

  // ── Price track highlight ──

  #updatePriceTrack(slider, minVal, maxVal) {
    const track = slider.querySelector('.dev-price-track');
    if (!track) return;
    const rangeMin = parseInt(slider.dataset.rangeMin, 10);
    const rangeMax = parseInt(slider.dataset.rangeMax, 10);
    const total = rangeMax - rangeMin || 1;
    const leftPct = ((minVal - rangeMin) / total) * 100;
    const rightPct = ((rangeMax - maxVal) / total) * 100;
    track.style.left = `${leftPct}%`;
    track.style.right = `${rightPct}%`;
  }

  #syncAllPriceSliders() {
    this.querySelectorAll('.dev-price-slider').forEach((slider) => {
      const minRange = slider.querySelector('[data-price-role="min"]');
      const maxRange = slider.querySelector('[data-price-role="max"]');
      if (minRange && maxRange) {
        this.#updatePriceTrack(
          slider,
          parseInt(minRange.value, 10),
          parseInt(maxRange.value, 10)
        );
      }
    });
  }

  // ── Filter submission (debounced, AJAX) ──

  #debouncedSubmit() {
    clearTimeout(this._filterTimeout);
    this._filterTimeout = setTimeout(() => this.#submitFilters(), 500);
  }

  #submitFilters() {
    const form = this.refs.filterForm;
    if (!form) return;

    const formData = new FormData(form);
    const url = new URL(form.action);

    for (const key of [...url.searchParams.keys()]) {
      if (key.startsWith('filter.') || key === 'sort_by') {
        url.searchParams.delete(key);
      }
    }

    for (const [key, value] of formData.entries()) {
      if (value !== '' && value !== null) {
        url.searchParams.append(key, value);
      }
    }

    url.searchParams.delete('page');
    this.#fetchAndReplace(url);
  }

  // ── Section Rendering API fetch ──

  async #fetchAndReplace(url) {
    if (this._isLoading) return;
    this._isLoading = true;

    if (this.refs.productArea) {
      this.refs.productArea.style.opacity = '0.5';
      this.refs.productArea.style.pointerEvents = 'none';
      this.refs.productArea.style.transition = 'opacity 0.2s';
    }

    history.pushState(null, '', url);

    try {
      const sectionId =
        this.dataset.sectionId ||
        this.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '');

      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('sections', sectionId);

      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Fetch failed');

      const data = await response.json();
      const html = data[sectionId];
      if (!html) throw new Error('Section not found');

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newComponent = doc.querySelector('dev-collection-component');
      if (!newComponent) throw new Error('Component not found');

      // Swap product area
      const newProductArea = newComponent.querySelector('[ref="productArea"]');
      if (newProductArea && this.refs.productArea) {
        this.refs.productArea.innerHTML = newProductArea.innerHTML;
      }

      // Swap product count
      const newCount = newComponent.querySelector('[ref="productCount"]');
      if (newCount && this.refs.productCount) {
        this.refs.productCount.innerHTML = newCount.innerHTML;
      }

      // Swap filter form
      const newFilterForm = newComponent.querySelector('[ref="filterForm"]');
      if (newFilterForm && this.refs.filterForm) {
        this.refs.filterForm.innerHTML = newFilterForm.innerHTML;
        // Re-sync price sliders after DOM swap
        this.#syncAllPriceSliders();
      }

      // Fade products in
      if (this.refs.productArea) {
        this.refs.productArea.style.opacity = '0';
        this.refs.productArea.style.pointerEvents = '';
        this.refs.productArea.offsetHeight;
        this.refs.productArea.style.transition = 'opacity 0.3s ease';
        this.refs.productArea.style.opacity = '1';
        this.refs.productArea.addEventListener(
          'transitionend',
          () => {
            this.refs.productArea.style.transition = '';
            this.refs.productArea.style.opacity = '';
          },
          { once: true }
        );
      }
    } catch (err) {
      console.error('Section fetch failed:', err);
      window.location.href = url;
    } finally {
      this._isLoading = false;
    }
  }
}

customElements.define('dev-collection-component', DevCollectionComponent);
